// src/pages/Admin/Dojos/EditDojoModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaBuilding,
  FaBook,
  FaMapMarkerAlt,
  FaListOl,
  FaUpload,
  FaTrashAlt,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateDojo } from "../../../services/dojoServices";
import { uploadDojoPhoto } from "../../../services/dojoPhotoServices";
import { toast } from "react-toastify";
import { toAbsolutePhotoUrl } from "./../../../services/dojoPhotoServices";

/* ---------- Helpers robustes ---------- */
// base API (Vite/CRA)
const API_BASE = (
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/$/, "");

// Absolutise uniquement /uploads/... ; laisse https://, data:, blob:, //... intacts.
// Gère aussi les assets front (/img/...)
function toAbsolute(u = "") {
  const s = String(u).trim();
  if (!s) return "";
  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:") ||
    s.startsWith("//")
  ) {
    return s;
  }
  if (!s.startsWith("/uploads")) return s;
  return API_BASE ? `${API_BASE}${s}` : s;
}

// Ajoute cache-bust avec le bon séparateur
function withCacheBust(u = "", v) {
  if (!u) return u;
  return u + (u.includes("?") ? "&" : "?") + "v=" + v;
}

const EditDojoModal = ({ isActive, onClose, refreshDojos, dojoData }) => {
  const [formData, setFormData] = useState({
    id: "",
    dojo_nom: "",
    dojo_discipline: "",
    dojo_adresse: "",
    dojo_photoURL: "",
    dojo_ordre: "",
  });

  const defaultDojo = "/img/no-image.jpg";
  const fallbackAvatar = "/img/dojo_placeholder.jpg";
  const [previewSrc, setPreviewSrc] = useState(defaultDojo);

  // Fichier local (optionnel)
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(""); // ObjectURL
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [imgVersion, setImgVersion] = useState(0); // ★ anti-cache, comme dans Team
  const dialogRef = useRef(null);

  // Hydrate form when dojoData changes
  useEffect(() => {
    if (!dojoData) return;
    const data = {
      id: dojoData.id || "",
      dojo_nom: dojoData.dojo_nom || "",
      dojo_discipline: dojoData.dojo_discipline || "",
      dojo_adresse: dojoData.dojo_adresse || "",
      dojo_photoURL: dojoData.dojo_photoURL || "",
      dojo_ordre: dojoData.dojo_ordre ?? "",
    };
    setFormData(data);
    // ★ preview: toAbsolute pour /uploads, sinon tel quel
    const initial = data.dojo_photoURL?.trim()
      ? data.dojo_photoURL
      : defaultDojo;
    setPreviewSrc(initial);
    setImgVersion((v) => v + 1);
  }, [dojoData]);

  // Ouverture : focus, ESC + lock scroll
  useEffect(() => {
    if (!isActive) return;

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='dojo_nom']")?.focus();
    }, 0);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onEsc);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Preview en fonction du fichier vs URL
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
      setPreviewSrc(url);
      setImgVersion((v) => v + 1); // ★ anti-cache
      return () => URL.revokeObjectURL(url);
    } else {
      const v = formData.dojo_photoURL?.trim()
        ? formData.dojo_photoURL
        : defaultDojo;
      setPreviewSrc(v);
      setFilePreview("");
      setImgVersion((vv) => vv + 1); // ★ force refresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const handleClose = () => {
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "dojo_photoURL" && !file) {
      const v = value.trim();
      setPreviewSrc(v ? v : defaultDojo);
      setImgVersion((vv) => vv + 1); // ★
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    const okType = /^image\/(png|jpe?g|webp)$/i.test(f.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    const input = dialogRef.current?.querySelector("input[type='file']");
    if (input) input.value = "";
    setImgVersion((vv) => vv + 1); // ★
  };

  // ★ On n'absolutise pas la valeur stockée : on envoie ce que l’utilisateur a saisi.
  //   C’est le backend qui connaît sa propre base, et nos pages d’affichage utiliseront toAbsolute au rendu.
  const buildPayload = () => {
    const photo = file
      ? formData.dojo_photoURL?.trim() || dojoData?.dojo_photoURL || ""
      : formData.dojo_photoURL?.trim() || dojoData?.dojo_photoURL || "";

    return {
      id: formData.id,
      dojo_nom: formData.dojo_nom.trim(),
      dojo_discipline: formData.dojo_discipline.trim(),
      dojo_adresse: formData.dojo_adresse.trim(),
      dojo_photoURL: photo, // <- peut être URL absolue externe, /img/... ou /uploads/...
      dojo_ordre:
        String(formData.dojo_ordre || "").trim() === ""
          ? 0
          : Number(formData.dojo_ordre),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id) {
      toast.error("ID du dojo introuvable !");
      return;
    }
    if (submitting) return;

    try {
      setSubmitting(true);
      setUploadProgress(0);

      const payload = buildPayload();
      if (!payload.dojo_nom) throw new Error("Le nom du dojo est requis.");
      if (!payload.dojo_discipline)
        throw new Error("La discipline est requise.");
      if (!payload.dojo_adresse) throw new Error("L'adresse est requise.");
      if (!Number.isFinite(payload.dojo_ordre))
        throw new Error("L'ordre d'affichage doit être un nombre.");

      // 1) Mettre à jour les champs texte + URL éventuelle
      await updateDojo(payload);
      toast.success("Dojo mis à jour avec succès");

      // 2) Si un fichier a été choisi -> uploadDojoPhoto (remplace côté serveur + met à jour dojo_photoURL)
      if (file) {
        try {
          await uploadDojoPhoto(formData.id, file, {
            format: "webp",
            onUploadProgress: (evt) => {
              if (!evt.total) return;
              const p = Math.round((evt.loaded / evt.total) * 100);
              setUploadProgress(p);
            },
          });
          toast.success("Nouvelle photo du dojo téléversée");
        } catch (err) {
          console.error("Upload photo dojo échoué:", err);
          toast.error(
            "Infos mises à jour, mais l'upload de la photo a échoué."
          );
        }
      }

      refreshDojos?.();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du dojo", error);
      toast.error(
        error?.response?.data ||
          error?.message ||
          "Erreur lors de la mise à jour du dojo"
      );
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (!isActive) return null;

  const absolutePhoto = toAbsolutePhotoUrl(formData.dojo_photoURL);

  // PORTAL (évite le clipping par des parents)
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier le dojo"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Modifier le Dojo
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Mettez à jour les informations du dojo.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable */}
        <form
          onSubmit={handleSubmit}
          className="grow overflow-y-auto p-4 sm:p-6"
        >
          <input type="hidden" name="id" value={formData.id} readOnly />

          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Preview + URL + Fichier */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              <div className="relative self-start">
                {/* ★ applique toAbsolute + cache-bust */}
                <img
                  src={`${absolutePhoto || defaultDojo}?v=${imgVersion}`}
                  alt="Aperçu du dojo"
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    e.currentTarget.src = fallbackAvatar;
                  }}
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />

                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  URL de la photo
                </label>
                <input
                  type="text"
                  name="dojo_photoURL"
                  value={formData.dojo_photoURL}
                  onChange={handleChange}
                  placeholder="https://…/dojo.jpg ou /uploads/dojo/xxx.webp"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  PNG/JPG/WebP recommandé. Format horizontal conseillé (ex:
                  1280×720).
                </p>

                {/* Upload fichier local (optionnel) */}
                <div className="mt-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <FaUpload /> Remplacer par un fichier (optionnel)
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileChange}
                    className="mt-1 block w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white hover:file:bg-violet-700"
                  />
                  {file && (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-600 dark:text-slate-300 truncate">
                        {file.name} ({Math.round(file.size / 1024)} Ko)
                      </span>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                        title="Retirer le fichier"
                      >
                        <FaTrashAlt /> Retirer
                      </button>
                    </div>
                  )}

                  {/* Barre de progression upload */}
                  {submitting && file && (
                    <div className="mt-2 w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-violet-600 transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Si une URL et un fichier sont fournis, le <b>fichier</b>{" "}
                    sera utilisé (prioritaire). Taille max : 10 Mo.
                  </p>
                </div>
              </div>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Nom */}
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom du dojo <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaBuilding />
                  </span>
                  <input
                    type="text"
                    name="dojo_nom"
                    value={formData.dojo_nom}
                    onChange={handleChange}
                    required
                    placeholder="Dojo de Plaisir"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Discipline */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Discipline <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaBook />
                  </span>
                  <input
                    type="text"
                    name="dojo_discipline"
                    value={formData.dojo_discipline}
                    onChange={handleChange}
                    required
                    placeholder="Judo, Ju-jitsu, etc."
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Adresse <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaMapMarkerAlt />
                  </span>
                  <input
                    type="text"
                    name="dojo_adresse"
                    value={formData.dojo_adresse}
                    onChange={handleChange}
                    required
                    placeholder="2 Rue de la République, 78370 Plaisir"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Ordre */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Ordre d’affichage <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="dojo_ordre"
                    value={formData.dojo_ordre}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Espace pour éviter que le contenu passe sous le footer sticky sur mobile */}
          <div className="h-12 sm:h-0" />
        </form>

        {/* Sticky actions footer */}
        <div className="sticky bottom-0 z-10 border-t border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
          <Button
            text="Annuler"
            icon={FaTimes}
            onClick={handleClose}
            type="button"
            className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
            disabled={submitting}
          />
          <Button
            text={submitting ? "Enregistrement..." : "Enregistrer"}
            icon={FaSave}
            type="submit"
            className="!rounded-xl gap-2"
            onClick={() =>
              dialogRef.current?.querySelector("form")?.requestSubmit()
            }
            disabled={submitting}
          />
        </div>
      </div>

      {/* Styles locaux : placeholders & scrollbar */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        .grow.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .grow.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }

        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditDojoModal;
